import type { CloudProductInfoDto, ProductInfoDto } from '@/castapi';
import { addMonths, differenceInDays, format, isBefore, subDays } from 'date-fns';
import { ISO_DATE_FORMAT, LEASE_STEPPING_ID, MDY_DATE_FORMAT, stripeLink, termsLink } from '.';
import { getExpiryOrSteppingDate, formatDateToMdy } from '@/utils';

export const newAutoRenewDate = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  if (product.expiryDate) {
    const expiryDate = new Date(product.expiryDate);
    const renewalDate = subDays(expiryDate, 4);
    return format(renewalDate, MDY_DATE_FORMAT);
  }
  return '';
};

export const chargePeriod = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  if ((product as CloudProductInfoDto).duration) {
    const duration = (product as CloudProductInfoDto).duration;
    if (duration === 1) {
      return 'every thirty (30) days';
    } else if (duration === 12) {
      return 'on the same day every year';
    }
    return `${duration} months`;
  }
  return 'on the same day every year';
};

export const lastDayToDisableAutoRenew = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  let expirationDate = product.expiryDate;
  if ((product as ProductInfoDto).steppingRef && (product as ProductInfoDto).steppingRef !== LEASE_STEPPING_ID) {
    expirationDate = getExpiryOrSteppingDate(product as ProductInfoDto);
  }
  const expiryDate = new Date(expirationDate);
  const duration = (product as CloudProductInfoDto).duration;
  let lastDay;
  let licenseIsTooNew = false;
  const createdAt = (product as CloudProductInfoDto).createdAt;
  if (createdAt) {
    licenseIsTooNew = differenceInDays(new Date(), new Date(createdAt)) < 30;
    if (licenseIsTooNew) {
      return format(subDays(expiryDate, 1), MDY_DATE_FORMAT);
    }
  }

  if (duration) {
    if (duration === 1) {
      lastDay = subDays(expiryDate, 1);
    }
  }
  lastDay = subDays(expiryDate, 31);
  if (isBefore(lastDay, new Date())) {
    const newExpireDate = addMonths(expiryDate, 12);
    lastDay = subDays(newExpireDate, 31);
  }
  return format(lastDay, MDY_DATE_FORMAT);
};

export const daysUntilExpiry = (product: ProductInfoDto | CloudProductInfoDto | undefined): number => {
  if (!product) {
    return 0;
  }
  let expirationDate = product.expiryDate;
  if ((product as ProductInfoDto).steppingRef && (product as ProductInfoDto).steppingRef !== LEASE_STEPPING_ID) {
    expirationDate = getExpiryOrSteppingDate(product as ProductInfoDto);
  }
  if (expirationDate) {
    const expiryDate = new Date(expirationDate);
    const today = new Date();
    return differenceInDays(expiryDate, today);
  }
  return 0;
};

export const expiryDateText = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return 'N/A';
  }
  let expirationDate = product.expiryDate;
  if ((product as ProductInfoDto).steppingRef && (product as ProductInfoDto).steppingRef !== LEASE_STEPPING_ID) {
    expirationDate = getExpiryOrSteppingDate(product as ProductInfoDto);
  }
  if (expirationDate) {
    return formatDateToMdy(expirationDate, ISO_DATE_FORMAT);
  }
  return 'N/A';
};

export const chargeDayText = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  let expirationDate = product.expiryDate;
  if ((product as ProductInfoDto).steppingRef && (product as ProductInfoDto).steppingRef !== LEASE_STEPPING_ID) {
    expirationDate = getExpiryOrSteppingDate(product as ProductInfoDto);
  }
  if (expirationDate) {
    const expiryDate = new Date(expirationDate);
    if (differenceInDays(expiryDate, new Date()) <= 4) {
      return 'today';
    }
    return `on ${newAutoRenewDate(product)}`;
  }
  return '';
};

export const lastARdayText = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  const duration = (product as CloudProductInfoDto).duration;
  if (duration) {
    if (duration === 1) {
      return `Should you wish to later disable automatic renewals, your last day to do so without getting charged for another monthly license per our ${termsLink}, will be ${lastDayToDisableAutoRenew(
        product,
      )}.`;
    } else if (duration === 12) {
      return `Your last day to disable Auto-Renew without getting charged will be ${lastDayToDisableAutoRenew(
        product,
      )}.`;
    }
  }
  return `Your last day to disable Auto-Renew without getting charged will be ${lastDayToDisableAutoRenew(product)}.`;
};

export const nextPeriodText = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  const duration = (product as CloudProductInfoDto).duration;
  if (duration) {
    if (duration === 1) {
      return '30 days';
    } else if (duration === 12) {
      return 'year';
    }
  }
  return 'year';
};

export const productRenewalDate = (product: ProductInfoDto | CloudProductInfoDto | undefined): string =>
  product ? formatDateToMdy(product?.renewalDate, ISO_DATE_FORMAT) : '';

export const autoRenewEnableNewLicenseConfirmationText = (
  product: ProductInfoDto | CloudProductInfoDto | undefined,
): string => {
  return `With Auto-Renew enabled, you will no longer have to worry about your license expiring. To enable automatic renewals, you must specify a Payment Method; this will appear in the ORGANIZATION tab and will be securely stored on ${stripeLink}.
<br/><br/>
The current expiry date for this license is ${expiryDateText(product)}.
<br/><br/>
Once automatic renewals are enabled, your Payment Method will be charged ${chargePeriod(
    product,
  )}, starting ${chargeDayText(product)}.
<br/><br/>
${lastARdayText(product)}
<br/><br/>
Are you sure you wish to ENABLE automatic renewals for this license?`;
};

export const autoRenewDisableNewLicenseConfirmationText = (
  product: ProductInfoDto | CloudProductInfoDto | undefined,
): string => {
  return `
Without automatic renewals, you will no longer be able to run the software
(if using a  Cloud License or a Lease dongle) or you will no longer be entitled to yearly updates
(if using a Membership dongle) after this license expires on ${expiryDateText(
    product,
  )}, unless you renew it manually or re-enable Auto-Renew.
(Note that Membership dongles are discontinued and no longer available for purchase.)
<br/><br/>
The Payment Method associated with this license will no longer be charged, but it will remain securely stored on ${stripeLink}. To remove it, click the ORGANIZATION tab and scroll down to the Payment Methods section.
<br/><br/>
Are you sure you wish to DISABLE automatic renewals for this license? `;
};

export const autoRenewEnableOldLicenseConfirmationText = (
  product: ProductInfoDto | CloudProductInfoDto | undefined,
): string => {
  return `With Auto-Renew enabled, you will no longer have to worry about your license expiring. To enable automatic renewals, you must specify a Payment Method; this will appear in the ORGANIZATION tab and will be securely stored on ${stripeLink}.
<br/><br/>
The current expiry date for this license is ${expiryDateText(product)}.
<br/><br/>
Once automatic renewals are enabled, your Payment Method will be charged ${chargePeriod(
    product,
  )}, starting ${chargeDayText(product)}.
<br/><br/>
${lastARdayText(product)}
<br/><br/>
Are you sure you wish to ENABLE automatic renewals for this license? `;
};

export const autoRenewDisableOldLicenseConfirmationText = (
  product: ProductInfoDto | CloudProductInfoDto | undefined,
): string => {
  if (!product) {
    return '';
  }
  return `
Without automatic renewals, you will no longer be able to run the software
(if using a  Cloud License or a Lease dongle) or you will no longer be entitled to yearly updates
(if using a Membership dongle) after this license expires on ${expiryDateText(
    product,
  )}, unless you renew it manually or re-enable Auto-Renew.
(Note that Membership dongles are discontinued and no longer available for purchase.)
<br/><br/>
Per our ${termsLink}, Auto-Renew must be disabled at least thirty (30) days before your license expires. Since your license expires ${daysUntilExpiry(
    product,
  )} days from today, you will be charged for another ${nextPeriodText(product)} on ${productRenewalDate(
    product,
  )}, but automatic renewals will stop after that.
<br/><br/>
The Payment Method associated with this license will remain securely stored on ${stripeLink}. To remove it, click the ORGANIZATION tab and scroll down to the Payment Methods section.
<br/><br/>
Are you sure you wish to DISABLE automatic renewals for this license?`;
};

export const autoRenewEnableConfirmationText = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  const createdAt = (product as CloudProductInfoDto).createdAt;
  if (createdAt) {
    const newLicense = differenceInDays(new Date(), new Date(createdAt)) < 30;
    if (newLicense) {
      return autoRenewEnableNewLicenseConfirmationText(product);
    }
  }
  if (differenceInDays(new Date(product.expiryDate), new Date()) > 30) {
    return autoRenewEnableNewLicenseConfirmationText(product);
  }
  return autoRenewEnableOldLicenseConfirmationText(product);
};

export const autoRenewDisableConfirmationText = (product: ProductInfoDto | CloudProductInfoDto | undefined): string => {
  if (!product) {
    return '';
  }
  if (product.canAutoRenewBeDisabled) {
    return autoRenewDisableNewLicenseConfirmationText(product);
  }
  return autoRenewDisableOldLicenseConfirmationText(product);
};

export const autoUpgradeDisableConfirmationText = (
  product: ProductInfoDto | CloudProductInfoDto | undefined,
): string => {
  if (!product) {
    return '';
  }
  return `With Auto-Upgrade disabled, you will no longer be able to run the software after this Free Trial expires on ${expiryDateText(product)}, unless you re-enable Auto-Upgrade, upgrade manually, or purchase a different license. The Payment Method associated with this Free Trial will not be charged, but it will remain securely stored on ${stripeLink}. (You may remove it after the Free Trial Expires. To do so, click the ORGANIZATION tab and scroll down to the Payment Methods section.)
<br/><br/>
Are you sure you wish to DISABLE Auto-Upgrade for this license?`;
};

export const autoUpgradeEnableConfirmationText = (
  product: ProductInfoDto | CloudProductInfoDto | undefined,
): string => {
  return `With Auto-Upgrade enabled, a new Monthly Perform Lite license will be added to your account automatically once this free license expires on ${expiryDateText(product)}. The Payment Method that you supplied when you signed up for the Free Trial, which is securely stored on Stripe, will be charged automatically for the cost of a Monthly Perform Lite license. Should you wish to purchase a license for a different product level, please do so by clicking the STORE tab.
<br/><br/>
Are you sure you wish to ENABLE Auto-Upgrade for this license? `;
};
